<script context="module" lang="ts">
    export type DualListItem = {
        id: string,
        label: string,
        disabled?: boolean,
        selected: boolean,
        marked?: boolean,
        order?: number,
        hidden?: boolean,
        multi?: boolean,
        _uq?: string
    };
</script>

<script lang="ts">
    import { _ } from 'svelte-i18n';

    export let items: DualListItem[];
    export let sortable: boolean = true;
    export let onChange: (id: string, selected: boolean, order: number|undefined) => void = (id, selected, order) => {};
    export let maxHeight: number = 250;

    let availableItems: DualListItem[] = [], selectedItems: DualListItem[] = [];

    let container: Element, buttonMoveToAvailable: HTMLButtonElement, buttonMoveToSelected: HTMLButtonElement;

    let sourceList: string|null = null;
    let targetList: string|null = null;

    let draggedElements: any[] = [];
    let dragging = false;
    let clientX: number, clientY: number;
    let orderBeforeDrag: {[key: string]: number | undefined} = {};

    let allIcon = 'data:image/svg+xml;base64,'+btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="-2.39 -4.14 28.68 27.239">
        <path d="M0,1H4.481V5.481H0ZM0,8.469H4.481V12.95H0Zm0,7.469H4.481v4.481H0ZM7.469,1H23.9V5.481H7.469Zm0,7.469H23.9V12.95H7.469Zm0,7.469H23.9v4.481H7.469Z" transform="translate(0 -1)" fill="#000"/>
    </svg>`);

    function onButtonMouseDown(e: any) {
        clientX = e.clientX;
        clientY = e.clientY;

        let item = items.filter((i) => i.id + (i._uq ? '~'+i._uq : '') === e.target.value)[0];
        sourceList = e.target.closest('.list').getAttribute('data-listtype');

        if (!item.disabled || ((item.order || 0) >= 0 && sortable && sourceList == 'selected')) {
            if (sourceList == 'selected') {
                orderBeforeDrag = {};
                selectedItems.forEach((i) => { orderBeforeDrag[i.id] = i.order; });
            }
            if (item.marked) {
                draggedElements = Array.from(container.querySelectorAll('.list[data-listtype='+sourceList+'] button.marked'));
            } else {
                draggedElements = [e.target];
            }
            let containerpos = container.getBoundingClientRect();
            let elempos = e.target.getBoundingClientRect();
            draggedElements = draggedElements.map((draggedElement) => {
                let _elempos = draggedElement.getBoundingClientRect();
                return {
                    element: draggedElement,
                    offset: [_elempos.left - containerpos.left, _elempos.top - containerpos.top]
                };
            });
            document.addEventListener('mousemove', dragElement);
            document.addEventListener('mouseup', stopDragging);
        }
    }

    function dragElement(e: any) {
        e.preventDefault();
        if (!dragging && (Math.abs(e.clientX - clientX) > 3 || Math.abs(e.clientY - clientY) > 3)) {
            let containerpos = container.getBoundingClientRect();
            draggedElements = draggedElements.map((draggedElement) => {
                draggedElement.originalElement = draggedElement.element;
                draggedElement.element = draggedElement.element.cloneNode(true);
                draggedElement.item = items.filter((i) => i.id + (i._uq ? '~'+i._uq : '') === draggedElement.element.value)[0];
                draggedElement.item.hidden = true;
                draggedElement.element.style.position = 'absolute';
                draggedElement.element.style.left = (e.clientX - clientX + draggedElement.offset[0]) + 'px';
                draggedElement.element.style.top = (e.clientY - clientY + draggedElement.offset[1]) + 'px';
                draggedElement.element.style.width = draggedElement.originalElement.getBoundingClientRect().width + 'px';
                draggedElement.element.style.pointerEvents = 'none';
                draggedElement.element.classList.add('dragging');
                container.appendChild(draggedElement.element);
                return draggedElement;
            });
            items = items;
            dragging = true;
        } else if (dragging) {
            draggedElements.forEach((draggedElement) => {
                draggedElement.element.style.left = (e.clientX - clientX + draggedElement.offset[0]) + 'px';
                draggedElement.element.style.top = (e.clientY - clientY + draggedElement.offset[1]) + 'px';
            });
        }
        let targetListElement = document.elementFromPoint(e.clientX, e.clientY)?.closest('.list');
        if (targetListElement) {
            targetList = targetListElement.getAttribute('data-listtype');
            if (targetList !== sourceList) {
                if (!draggedElements[0].item.disabled) {
                    targetListElement.classList.add('droppable');
                }
            } else if (sortable && targetList == 'selected' && draggedElements.length == 1) {
                let item = items.filter((i) => i.id + (i._uq ? '~'+i._uq : '') === draggedElements[0].element.value)[0];
                let currentrect = draggedElements[0].element.getBoundingClientRect();
                let elements = container.querySelectorAll('.list[data-listtype='+targetList+'] button');
                let originalOrder = item.order;
                let insertBefore = elements.length
                for (let i = 0; i < elements.length; i++) {
                    let rect = elements[i].getBoundingClientRect();
                    if (rect.top > currentrect.top) {
                        let beforeItem = items.filter((it) => it.id + (it._uq ? '~'+it._uq : '') === elements[i].getAttribute('value'))[0];
                        if (typeof(beforeItem.order) === 'number' && beforeItem.order >= 0) {
                            insertBefore = beforeItem.order;
                            break;
                        }
                    }
                }
                if (typeof(originalOrder) === 'number' && originalOrder != insertBefore) {
                    items = items.map((i) => {
                        if (i === item) {
                            item.order = insertBefore - (originalOrder < insertBefore ? 1 : 0);
                        } else {
                            if (originalOrder > insertBefore) {
                                if (typeof(i.order) == 'number' && i.order >= insertBefore && i.order < originalOrder) {
                                    i.order++;
                                }
                            } else {
                                if (typeof(i.order) == 'number' && i.order > originalOrder && i.order < insertBefore) {
                                    i.order--;
                                }
                            }
                        }
                        return i;
                    });
                }
            }
        } else {
            targetList = null;
        }
        container.querySelectorAll('.list.droppable').forEach((list) => {
            if (list.getAttribute('data-listtype') !== targetList) {
                list.classList.remove('droppable');
            }
        });
    }

    function stopDragging(e: any) {
        document.removeEventListener('mousemove', dragElement);
        document.removeEventListener('mouseup', stopDragging);
        if (dragging) {
            container.querySelectorAll('.list.droppable').forEach((list) => {
                list.classList.remove('droppable');
            });
            draggedElements.forEach((draggedElement) => {
                if (targetList) {
                    let item = items.filter((i) => i.id + (i._uq ? '~'+i._uq : '') === draggedElement.element.value)[0];
                    if (targetList != sourceList) {
                        if (!item.disabled) {
                            if (sourceList == 'selected') {
                                for (let id in orderBeforeDrag) {
                                    selectedItems.filter((i) => i.id == id)[0].order = orderBeforeDrag[id];
                                };
                            }
                            if (item.multi) {
                                if (targetList == 'selected') {
                                    let newitem = Object.assign({}, item);
                                    newitem.selected = true;
                                    newitem.hidden = false;
                                    newitem._uq = Date.now().toString(16);
                                    items.push(newitem);
                                } else {
                                    items.splice(items.indexOf(item), 1);
                                }
                            } else {
                                item.selected = !item.selected;
                            }
                            onChange(item.id, item.selected, item.order);
                        }
                    } else if (sortable && targetList == 'selected' && draggedElements.length == 1) {
                        let currentrect = draggedElements[0].element.getBoundingClientRect();
                        let elements = container.querySelectorAll('.list[data-listtype='+targetList+'] button');
                        let originalOrder = orderBeforeDrag[item.id];
                        let insertBefore = elements.length;
                        for (let i = 0; i < elements.length; i++) {
                            let rect = elements[i].getBoundingClientRect();
                            if (rect.top > currentrect.top) {
                                let beforeItem = items.filter((it) => it.id + (it._uq ? '~'+it._uq : '') === elements[i].getAttribute('value'))[0];
                                if (typeof(beforeItem.order) === 'number' && beforeItem.order >= 0) {
                                    insertBefore = beforeItem.order;
                                }
                                break;
                            }
                        }
                        if (originalOrder != insertBefore) {
                            items = items.map((i) => {
                                if (i.order != orderBeforeDrag[i.id]) {
                                    onChange(i.id, i.selected, i.order);
                                }
                                return i;
                            });
                        }
                    }
                }
                draggedElement.element.remove();
                draggedElement.item.hidden = false;
            });
            draggedElements = [];
            dragging = false;
            items = items.map((i) => { i.marked = false; return i; });
            items = items;
        }
    }

    function markItem(e: any) {
        let item = items.filter((i) => i.id + (i._uq ? '~'+i._uq : '') === e.target.value)[0];
        if (!item.disabled) {
            sourceList = e.target.closest('.list').getAttribute('data-listtype');
            items.forEach((i) => {
                if (i === item) {
                    if (1 || e.ctrlKey) {
                        item.marked = !item.marked;
                    } else {
                        item.marked = true;
                    }
                } else {
                    if (!e.ctrlKey || i.selected !== item.selected) {
                        i.marked = false;
                    }
                }
            });
            items = items;
        }
    }

    function moveItem(e: any) {
        e.preventDefault();
        let item = items.filter((i) => i.id + (i._uq ? '~'+i._uq : '') === e.target.value)[0];
        if (!item.disabled) {
            item.marked = true;
            if (!e.ctrlKey) {
                items = items.map((i) => { i.marked = i === item; return i;})
            }
            moveItems(!item.selected);
        }
    }

    function moveItems(selected: boolean) {
        let newitems: DualListItem[] = [];
        let removeditems: DualListItem[] = [];
        items = items.map((item) => {
            if (item.marked) {
                item.marked = false;
                item.order = undefined;
                if (item.multi) {
                    if (selected) {
                        let newitem = Object.assign({}, item);
                        newitem.selected = true;
                        newitem._uq = Date.now().toString(16);
                        newitems.push(newitem);
                    } else {
                        item.selected = false;
                        removeditems.push(item);
                    }
                } else {
                    item.selected = selected;
                }
                onChange(item.id, item.selected, undefined);
            }
            return item;
        });
        for (let item of newitems) {
            items.push(item);
        }
        for (let item of removeditems) {
            items.splice(items.indexOf(item), 1);
        }
    }

    function moveAll(selected: boolean) {
        items = items.map((item) => {
            if (item.selected === !selected && !item.disabled) {
                item.marked = true;
            } else {
                item.marked = false;
            }
            return item;
        });
        moveItems(selected);
    }

    $: {
        items = items;
        availableItems = items.filter((i) => !i.selected).map((i) => { i.order = undefined; return i});
        selectedItems = items.filter((i) => i.selected);
        let nextOrderValue = 0;
        let orderedElements = selectedItems.map((i) => i.order).filter((o) => typeof(o) == 'number');
        if (orderedElements.length) {
            nextOrderValue = Math.max(...orderedElements) + 1;
        }
        selectedItems = selectedItems.map((item) => {
            if (typeof(item.order) != 'number') {
                item.order = nextOrderValue;
                onChange(item.id, item.selected, item.order);
                nextOrderValue++;
            }
            return item;
        });
        selectedItems.sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
        selectedItems.forEach((item, idx) => {
            if (typeof(item.order) == 'number' && item.order >= 0 && item.order != idx) {
                selectedItems[idx].order = idx;
                onChange(selectedItems[idx].id, selectedItems[idx].selected, selectedItems[idx].order);
            }
        })
        let marked = items.filter((i) => i.marked);
        if (buttonMoveToAvailable) {
            buttonMoveToAvailable.disabled = marked.length == 0 || !marked[0].selected;
        }
        if (buttonMoveToSelected) {
            buttonMoveToSelected.disabled = marked.length == 0 || marked[0].selected;
        }
    }

</script>

<div class="dual-listbox" bind:this={container} style="max-height: {maxHeight}px">
    <!-- Available Items -->
    <div
      class="list"
      data-listtype="available"
    >
      <strong>{$_('duallist-available')}</strong>
      {#each availableItems as item}
        <button
          class:disabled={item.disabled}
          class:marked={item.marked}
          on:mousedown={onButtonMouseDown}
          on:click={markItem}
          on:dblclick={moveItem}
          value={item.id}
        >
          {@html item.label}
        </button>
      {/each}
    </div>

    <!-- Transfer Buttons -->
    <div class="buttons">
      <button on:click={() => moveAll(true)} title={$_('duallist-select-all')}><img src={allIcon} style="height: 16px; vertical-align: middle" alt=""/> &#9654;</button>
      <button bind:this={buttonMoveToSelected} on:click={() => moveItems(true)} disabled title={$_('duallist-select-marked')}>&#9654;</button>
      <button bind:this={buttonMoveToAvailable} on:click={() => moveItems(false)} disabled title={$_('duallist-unselect-marked')}>&#9664;</button>
      <button on:click={() => moveAll(false)} title={$_('duallist-unselect-all')}>&#9664; <img src={allIcon} style="height: 16px; vertical-align: middle" alt=""/></button>
    </div>

    <!-- Selected Items -->
    <div
      class="list"
      data-listtype="selected"
    >
      <strong>{$_('duallist-selected')}</strong>
      {#each selectedItems as item, index}
        <button
          class:disabled={item.disabled}
          class:marked={item.marked}
          class:hidden={item.hidden}
          on:mousedown={onButtonMouseDown}
          on:click={markItem}
          on:dblclick={moveItem}
          value={item.id+(item._uq ? '~'+item._uq : '')}
        >
          {item.label}
        </button>
      {/each}
    </div>
</div>

<style>
    .dual-listbox {
        display: flex;
        gap: 20px;
        align-items: center;
        position: relative;
    }

    .list {
        flex: 1;
        align-self: stretch;
        border: 1px solid #ccc;
        border-radius: 3px;
        overflow-y: auto;
        padding: 10px;
        background: #f9f9f9;
    }

    .dual-listbox button {
        display: block;
        width: 100%;
        margin: 5px 0;
        padding: 5px;
        text-align: left;
        background: white;
        border: 1px solid #ddd;
        border-radius: 3px;
        cursor: pointer;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .disabled {
        color: gray;
        background: #eee;
        opacity: 0.5;
    }

    .dual-listbox button.disabled {
        cursor: auto;
    }

    .list button.marked {
        color: #fff;
        background-color: #007bff;
    }

    .buttons {
        display: flex;
        flex-direction: column;
        gap: 10px;
        width: 45px;
    }

    .dual-listbox :global(.droppable) {
        border: 2px dashed #007bff;
    }

    .dual-listbox :global(.hidden) {
        visibility: hidden;
    }

    .buttons button {
        text-align: center;
    }

</style>
